import React, { useState, useEffect, useCallback } from 'react';
import {
  Waypoints, ArrowUpRight, ArrowDownLeft, CheckCircle, XCircle, Clock,
  RefreshCw, Loader2, AlertCircle, AlertTriangle, X, Copy, Check,
  ArrowRightLeft, Search, ArrowDownToLine, Code2,
} from 'lucide-react';
import { ledgerApi } from '../../services/iso20022Api';

// ── small presentational helpers ────────────────────────────────────────────

const OP_META = {
  transfer:     { label: 'Transfer',     cls: 'bg-blue-50 text-blue-700',     Icon: ArrowRightLeft  },
  lookup:       { label: 'Lookup',       cls: 'bg-violet-50 text-violet-700', Icon: Search          },
  registration: { label: 'Registration', cls: 'bg-teal-50 text-teal-700',     Icon: ArrowDownToLine },
  other:        { label: 'Other',        cls: 'bg-gray-100 text-gray-600',    Icon: Waypoints       },
};

const OP_FILTERS = [
  { id: '',             label: 'All' },
  { id: 'transfer',     label: 'Transfers' },
  { id: 'lookup',       label: 'Lookups' },
  { id: 'registration', label: 'Registrations' },
];

// txStatus / status → colour. Rejections are red but legitimate; malformed is a fault (amber).
function txClass(s) {
  const u = (s || '').toUpperCase();
  if (['ACSC', 'VERIFIED', 'ACCP', 'COMMITTED'].includes(u)) return 'bg-green-100 text-green-800';
  if (['RJCT', 'NOT_VERIFIED', 'ABORTED'].includes(u))       return 'bg-red-100 text-red-700';
  if (u === 'MALFORMED')                                      return 'bg-amber-100 text-amber-800';
  return 'bg-gray-100 text-gray-600';
}

function fmtMs(ms) {
  if (ms == null) return '';
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  return `${s.toFixed(s < 10 ? 1 : 0)}s`;
}

function OperationBadge({ operation }) {
  const m = OP_META[operation] || OP_META.other;
  const { Icon } = m;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md ${m.cls}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

function DirectionBadge({ direction }) {
  const toSwitch = direction === 'TO_SWITCH';
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md ${
      toSwitch ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
      {toSwitch ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownLeft className="w-3 h-3" />}
      {toSwitch ? 'CBS → Switch' : 'Switch → CBS'}
    </span>
  );
}

// ── per-hop signed-XML expander (lazy loads the existing detail endpoint) ────

function HopXml({ hop }) {
  const [open, setOpen]     = useState(false);
  const [data, setData]     = useState(null);
  const [error, setError]   = useState(null);
  const [copied, setCopied] = useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && data == null && error == null) {
      try {
        setData(await ledgerApi.detail(hop.messageType, hop.id));
      } catch (e) {
        setError(e.response?.data?.message || e.message);
      }
    }
  };

  const copy = () => {
    if (!data?.rawXml) return;
    navigator.clipboard.writeText(data.rawXml);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mt-2">
      <button onClick={toggle}
        className="flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700">
        <Code2 className="w-3.5 h-3.5" /> {open ? 'Hide' : 'View'} signed XML
      </button>
      {open && (
        <div className="mt-2">
          {!data && !error && (
            <div className="flex items-center gap-2 text-xs text-gray-500 py-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
            </div>
          )}
          {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}
          {data && (
            <div className="relative">
              <button onClick={copy}
                className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 text-[11px] rounded-md bg-white/90 border border-gray-200 text-gray-600 hover:bg-gray-50">
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}{copied ? 'Copied' : 'Copy'}
              </button>
              <pre className="bg-gray-900 text-gray-100 text-[11px] rounded-lg p-3 overflow-auto max-h-72 whitespace-pre-wrap break-all">
                {data.rawXml || '(no payload stored)'}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── the trace timeline drawer ───────────────────────────────────────────────

function TraceDrawer({ summary, onClose }) {
  const [trace, setTrace] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!summary) return;
    setTrace(null); setError(null);
    ledgerApi.trace(summary.corrKey)
      .then(setTrace)
      .catch(e => setError(e.response?.data?.message || e.message));
  }, [summary]);

  if (!summary) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-gray-900/40" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <OperationBadge operation={summary.operation} />
            <span className="font-mono text-sm text-gray-700 truncate">{summary.reference || summary.corrKey}</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 shrink-0"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {!trace && !error && (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading trace…
            </div>
          )}
          {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>}

          {trace && (
            <>
              {/* summary strip */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  ['Hops', trace.hopCount],
                  ['Elapsed', fmtMs(trace.elapsedMs) || '—'],
                  ['Result', trace.txStatus || trace.status || '—'],
                  ['Amount', trace.amount ? `${trace.amount} ${trace.currency || ''}`.trim() : '—'],
                ].map(([k, v]) => (
                  <div key={k} className="bg-gray-50 rounded-lg p-2">
                    <p className="text-xs text-gray-500">{k}</p>
                    <p className="text-sm text-gray-900 font-medium break-all">{v}</p>
                  </div>
                ))}
              </div>
              {trace.hasFault && (
                <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  This trace contains an injected fault — excluded from certification scoring.
                </div>
              )}

              {/* timeline */}
              <div>
                <p className="text-sm font-medium text-gray-900 mb-3">Timeline</p>
                <ol className="relative border-l-2 border-gray-100 ml-2">
                  {trace.hops.map((h, i) => (
                    <li key={h.id ?? i} className="ml-4 pb-5 last:pb-0">
                      <span className={`absolute -left-[7px] mt-1.5 w-3 h-3 rounded-full border-2 border-white ${
                        h.fault ? 'bg-amber-400' : h.direction === 'TO_SWITCH' ? 'bg-blue-400' : 'bg-purple-400'}`} />
                      <div className="bg-white border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{h.messageType}</span>
                            <DirectionBadge direction={h.direction} />
                            {(h.txStatus || h.status) && (
                              <span className={`text-xs px-2 py-0.5 rounded-md ${txClass(h.txStatus || h.status)}`}>
                                {h.txStatus || h.status}
                              </span>
                            )}
                            {h.fault && (
                              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md bg-amber-100 text-amber-800">
                                <AlertTriangle className="w-3 h-3" /> fault
                              </span>
                            )}
                          </div>
                          <span className="inline-flex items-center gap-1 text-xs text-gray-400 font-mono" title="time since first hop">
                            <Clock className="w-3 h-3" /> +{fmtMs(h.sinceStartMs) || '0ms'}
                            {i > 0 && h.sincePrevMs != null && <span className="text-gray-300">· Δ{fmtMs(h.sincePrevMs)}</span>}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                          <span className="inline-flex items-center gap-1">
                            {h.signatureOk
                              ? <><CheckCircle className="w-3.5 h-3.5 text-green-500" /> signature valid</>
                              : <><XCircle className="w-3.5 h-3.5 text-gray-300" /> {h.signatureOk === false ? 'unsigned / not verified' : 'n/a'}</>}
                          </span>
                          {h.caseIds && <span className="font-mono text-[11px] text-gray-400">cases: {h.caseIds}</span>}
                          <span className="font-mono text-[11px] text-gray-400">{h.createdAt}</span>
                        </div>
                        <HopXml hop={h} />
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────

export default function Traces() {
  const [traces, setTraces]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [op, setOp]           = useState('');
  const [faultsOnly, setFaultsOnly] = useState(false);
  const [q, setQ]             = useState('');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setTraces(await ledgerApi.traces(100));
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = traces.filter(t =>
    (!op || t.operation === op) &&
    (!faultsOnly || t.hasFault) &&
    (!q || (t.reference || '').toLowerCase().includes(q.toLowerCase()) ||
           (t.messageChain || '').toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Waypoints className="w-6 h-6 text-primary-600" /> Async Traces
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Trace a transaction end-to-end — every request, response and callback in one timeline,
            with per-hop timing and signature status. The callback inspector for asynchronous behaviour.
          </p>
        </div>
        <button onClick={load} className="shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
          {OP_FILTERS.map(f => (
            <button key={f.id} onClick={() => setOp(f.id)}
              className={`px-3 py-2 transition-colors ${
                op === f.id ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              {f.label}
            </button>
          ))}
        </div>
        <input value={q} onChange={e => setQ(e.target.value)}
          placeholder="Search reference or message type"
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 w-72" />
        <label className="flex items-center gap-2 text-sm text-gray-600 select-none cursor-pointer">
          <input type="checkbox" checked={faultsOnly} onChange={e => setFaultsOnly(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
          Faults only
        </label>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2.5 font-medium">Last activity</th>
              <th className="text-left px-4 py-2.5 font-medium">Operation</th>
              <th className="text-left px-4 py-2.5 font-medium">Reference</th>
              <th className="text-left px-4 py-2.5 font-medium">Chain</th>
              <th className="text-left px-4 py-2.5 font-medium">Result</th>
              <th className="text-left px-4 py-2.5 font-medium">Elapsed</th>
              <th className="text-left px-4 py-2.5 font-medium">Hops</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No traces match these filters.</td></tr>
            )}
            {!loading && rows.map(t => (
              <tr key={t.corrKey} onClick={() => setSelected(t)}
                  className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer">
                <td className="px-4 py-2.5 text-gray-500 font-mono text-xs whitespace-nowrap">{t.lastAt}</td>
                <td className="px-4 py-2.5"><OperationBadge operation={t.operation} /></td>
                <td className="px-4 py-2.5 font-mono text-xs text-gray-600 whitespace-nowrap">{t.reference || '—'}</td>
                <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">{t.messageChain}</td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-1.5">
                    {(t.txStatus || t.status) && (
                      <span className={`text-xs px-2 py-0.5 rounded-md ${txClass(t.txStatus || t.status)}`}>{t.txStatus || t.status}</span>
                    )}
                    {t.hasFault && <AlertTriangle className="w-3.5 h-3.5 text-amber-500" title="contains an injected fault" />}
                  </div>
                </td>
                <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">{fmtMs(t.elapsedMs) || '—'}</td>
                <td className="px-4 py-2.5 text-gray-700">{t.hopCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!loading && rows.length > 0 && (
        <p className="text-xs text-gray-400">{rows.length} transaction(s) · click a row for the full async timeline</p>
      )}

      <TraceDrawer summary={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
